import { performanceDiagnostics } from '@/diagnostics/browserDiagnostics';
import { DebugTimers } from './DebugTimers';
import { captureClockScheduler, ClockNode } from './nodes/ClockNode';
import { captureDelayScheduler } from './nodes/DelayNode';
import {
  checkpointTopology,
  validateSimulationCheckpoint,
  type SimulationCheckpoint,
  type NodeSchedulerCheckpoint,
} from './SimulationCheckpoint';
import { authorities } from '@/simulation/native/authority';
import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import { makeAutoObservable } from 'mobx';
import { LogicGraph, type SimulationNode } from './LogicGraph';
import { SimulationWorkSet } from './SimulationWorkSet';
import { ProcessedNodes } from './ProcessedNodes';
import type { LogicNodeInstance } from './LogicNodeInstance';
import { deepClone } from '@/utils/clone';
import {
  scheduleSimulationTask,
  restoreSimulationTask,
  captureSimulationTask,
  type SimulationTaskCheckpoint,
} from './scheduleSimulationTask';
import { SimulationEventQueue, type SimulationTiming } from './SimulationEventQueue';
import { DelayNode } from './nodes/DelayNode';

export class LogicSimulation {
  graph: LogicGraph;
  readonly debugTimers = new DebugTimers();
  isRunning = true;
  lastTickTime = 0;
  /** Completed live node evaluations, including repeated propagation and manual
   * steps. Excludes topology dry-runs; not CPU instructions or logical clock ticks.
   * Plain scalar: reading diagnostics must not observe every engine evaluation. */
  nodeEvaluationCount = 0;
  timerId: (() => void) | null = null;
  onPreUpdate?: (dirtyNodeIds?: ReadonlySet<NodeHandle>) => void;
  onPostUpdate?: (processedNodeIds: ReadonlySet<NodeHandle>) => void;
  onFlushVisuals?: () => void;
  /** Worker-only optional Boolean evaluator. Scheduling and publication stay here. */
  evaluateBooleanWave?: (
    graph: LogicGraph,
    entries: readonly SimulationNode[],
  ) => readonly boolean[];
  dirtyNodes: Set<NodeHandle> = new SimulationWorkSet(() => this.graph);

  // Configuration
  maxSyncIterations = 1;
  asyncDelay = 0;

  private isTicking = false;
  private diagnosticsSuppressed = 0;
  private continuationSuspended = false;
  private suspendedProcessNow = 0;
  private suspendedWallCursor = 0;
  private suspendedTick: SimulationTaskCheckpoint | null = null;
  private suspendedEventWake: SimulationTaskCheckpoint | null = null;
  private nodeProcessTimes = new Map<LogicNodeInstance, { last: number }>();
  // Tokens identify a timing generation without making graph handles retain this simulation.
  private timingOwner = {};
  private readonly clockOwner = {};
  private processedListeners = new Set<(ids: ReadonlySet<NodeHandle>) => void>();
  private eventTime = Date.now();
  private wallTimeOffset = 0;
  private maximumSpeedEnabled = false;
  private pendingMaximumSpeed: boolean | undefined;
  private modeBoundaryDepth = 0;
  private propagationWaves = 0;
  private timedEvents = new SimulationEventQueue(
    () =>
      this.continuationSuspended ||
      this.isTicking ||
      !this.isRunning ||
      (this.asyncDelay === 0 &&
        this.dirtyNodes.size > 0 &&
        this.propagationWaves < 10_000),
    (at, callbacks) => {
      this.modeBoundaryDepth++;
      try {
        this.eventTime = at;
        // Equal deadlines are one input snapshot, not serial latch transitions.
        this.isTicking = true;
        try {
          for (const callback of callbacks) callback();
        } finally {
          this.isTicking = false;
        }
        this.scheduleTick();
      } finally {
        this.finishModeBoundary();
      }
    },
    { isUnpaced: () => this.maximumSpeed, wallNow: () => this.wallNow() },
  );

  constructor(graph: LogicGraph) {
    // Exclude hot-path fields from MobX tracking to reduce overhead.
    // dirtyNodes and graph are accessed frequently during simulation ticks.
    makeAutoObservable<
      this,
      | 'continuationSuspended'
      | 'suspendedProcessNow'
      | 'suspendedWallCursor'
      | 'suspendedTick'
      | 'suspendedEventWake'
      | 'diagnosticsSuppressed'
      | 'isTicking'
      | 'nodeProcessTimes'
      | 'processedListeners'
      | 'timingOwner'
      | 'clockOwner'
      | 'elapsedForNode'
      | 'eventTime'
      | 'wallTimeOffset'
      | 'maximumSpeedEnabled'
      | 'pendingMaximumSpeed'
      | 'modeBoundaryDepth'
      | 'wallNow'
      | 'finishModeBoundary'
      | 'propagationWaves'
      | 'timedEvents'
    >(this, {
      graph: false,
      debugTimers: false,
      // Cancellation identity carries internal pending-task checkpoint metadata.
      timerId: false,
      continuationSuspended: false,
      suspendedProcessNow: false,
      suspendedWallCursor: false,
      suspendedTick: false,
      isTicking: false,
      diagnosticsSuppressed: false,
      suspendedEventWake: false,
      hasSuspendedContinuation: false,
      nodeEvaluationCount: false,
      nodeProcessTimes: false,
      timingOwner: false,
      clockOwner: false,
      elapsedForNode: false,
      processedListeners: false,
      eventTime: false,
      wallTimeOffset: false,
      maximumSpeedEnabled: true,
      pendingMaximumSpeed: false,
      modeBoundaryDepth: false,
      maximumSpeed: true,
      wallNow: false,
      finishModeBoundary: false,
      propagationWaves: false,
      timedEvents: false,
      dirtyNodes: false,
      onPreUpdate: false,
      onPostUpdate: false,
      onFlushVisuals: false,
      evaluateBooleanWave: false,
    });
    this.graph = graph;
    this.markAllNodesDirty();
  }

  /** Optional extension observers reuse the dirty-node result; no graph scan. */
  subscribeProcessed(listener: (ids: ReadonlySet<NodeHandle>) => void): () => void {
    this.processedListeners.add(listener);
    return () => {
      this.processedListeners.delete(listener);
    };
  }

  private publishProcessed(ids: ReadonlySet<NodeHandle>) {
    if (ids instanceof ProcessedNodes) ids.finish();
    this.onPostUpdate?.(ids);
    for (const listener of this.processedListeners) listener(ids);
  }

  setGraph(graph: LogicGraph) {
    this.debugTimers.cancel();
    this.diagnosticsSuppressed++;
    try {
      this.replaceGraph(graph);
    } finally {
      this.diagnosticsSuppressed--;
    }
  }

  private replaceGraph(graph: LogicGraph) {
    this.cancelPendingTick();
    this.eventTime = this.wallNow();
    this.propagationWaves = 0;
    // A graph can be revisited after its timing records were discarded.
    this.timingOwner = {};
    const retained = new Set<NodeHandle>();
    for (const [id, node] of this.graph.nodes) {
      if (
        this.isRunning &&
        graph.nodes.get(id) === node &&
        this.nodeProcessTimes.has(node)
      )
        retained.add(id);
      else {
        node.dispose();
        this.nodeProcessTimes.delete(node);
      }
    }
    this.graph = graph;
    const pending = [...this.dirtyNodes];
    this.dirtyNodes.clear();
    for (const id of pending) if (retained.has(id)) this.dirtyNodes.add(id);

    // Reset so the first tick after rebuild uses near-zero deltaTime
    // instead of advancing Clock/Delay node state with stale elapsed time.
    this.lastTickTime = this.now();

    if (this.isRunning) {
      this.attachTriggers(retained);
      this.markInconsistentNodesDirty();
      if (this.dirtyNodes.size > 0) {
        this.scheduleTick();
      }
    }
  }

  /**
   * After a rebuild with state restoration, only mark nodes whose current
   * output is inconsistent with their inputs. This avoids reprocessing the
   * entire graph when a benign change (adding a disconnected wire) occurs.
   *
   * Uses deltaTime=0 dry-run to avoid advancing stateful nodes.
   */
  private markInconsistentNodesDirty() {
    for (const [nodeId, node] of this.graph.nodes) {
      const input = this.graph.getNodeInput(nodeId);

      if (node.definition.needsInputSync?.(input, node.state)) {
        this.dirtyNodes.add(nodeId);
        continue;
      }

      // Call definition.process on a CLONED state to avoid side effects on
      // live node state. Stateless nodes (null state) need no clone.
      const scratchState = node.state != null ? deepClone(node.state) : node.state;
      const wouldProduce = node.definition.process(input, scratchState, 0);

      if (wouldProduce !== node.output) {
        this.dirtyNodes.add(nodeId);
      }
    }
  }

  start() {
    if (authorities.has(this)) throw Error('Use ordered worker Run');
    if (this.continuationSuspended) this.activateContinuation();
    if (this.isRunning) return;
    this.isRunning = true;
    this.lastTickTime = this.now();
    this.nodeProcessTimes.clear();
    this.timingOwner = {};
    this.attachTriggers();
    this.markAllNodesDirty();
    this.triggerUpdate();
  }

  stop() {
    if (authorities.has(this)) throw Error('Use ordered worker Pause');
    this.debugTimers.cancel();
    this.isRunning = false;
    this.continuationSuspended = false;
    this.timedEvents.resume();
    this.cancelPendingTick();
    this.dirtyNodes.clear();
    this.propagationWaves = 0;
    this.nodeProcessTimes.clear();
    this.timingOwner = {};
    for (const node of this.graph.nodes.values()) {
      node.dispose();
    }
    this.onFlushVisuals?.();
  }

  private now() {
    if (this.continuationSuspended && !this.maximumSpeed) return this.suspendedProcessNow;
    if (this.maximumSpeed) return this.eventTime;
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  private wallNow() {
    if (this.continuationSuspended) return this.suspendedWallCursor;
    return this.maximumSpeed ? this.eventTime : Date.now() + this.wallTimeOffset;
  }

  /** Explicit opt-in; requests made by callbacks commit at the outer work boundary. */
  get maximumSpeed() {
    return this.maximumSpeedEnabled;
  }

  setMaximumSpeed(value: boolean) {
    if (this.modeBoundaryDepth > 0) {
      this.pendingMaximumSpeed = value;
      return;
    }
    if (value === this.maximumSpeedEnabled) return;
    this.cancelPendingTick();
    // Keep the current logical cursor on entry; overdue events still own their timestamps.
    if (!value) this.wallTimeOffset = this.eventTime - Date.now();
    this.maximumSpeedEnabled = value;
    this.nodeProcessTimes.clear();
    this.timingOwner = {};
    this.lastTickTime = this.now();
    this.timedEvents.wake();
    // A publication callback may request another flip on every feedback wave.
    // Requeue work instead of recursively entering tick from the mode commit.
    if (this.isRunning && this.dirtyNodes.size > 0)
      this.armContinuationTick(this.asyncDelay);
  }

  private finishModeBoundary() {
    if (--this.modeBoundaryDepth !== 0 || this.pendingMaximumSpeed === undefined) return;
    const value = this.pendingMaximumSpeed;
    this.pendingMaximumSpeed = undefined;
    this.setMaximumSpeed(value);
  }

  private cancelPendingTick() {
    if (this.timerId !== null) {
      this.timerId();
      this.timerId = null;
    }
  }

  private markAllNodesDirty() {
    for (const id of this.graph.nodes.keys()) {
      this.dirtyNodes.add(id);
    }
  }

  private armContinuationTick(delay: number, restored?: SimulationTaskCheckpoint) {
    this.timerId = restored
      ? restoreSimulationTask(this.tick, restored)
      : scheduleSimulationTask(this.tick, delay);
  }

  /** Passive, versioned complete native runtime capture at an outer work boundary. */
  captureContinuation(): SimulationCheckpoint {
    if (
      this.isTicking ||
      this.modeBoundaryDepth !== 0 ||
      this.pendingMaximumSpeed !== undefined
    )
      throw Error('Continuation requires an outer simulation boundary');
    const now = this.now();
    const task = captureSimulationTask(this.timerId);
    const wake = this.timedEvents.captureWake();
    const eventWake = wake
      ? { ...wake, order: task && task.order < wake.order ? 1 : 0 }
      : null;
    const tick = task
      ? { ...task, order: wake && wake.order < task.order ? 1 : 0 }
      : null;
    const result: SimulationCheckpoint = {
      version: 1,
      abi: 'digitiles-native-events-1',
      topology: checkpointTopology(this.graph),
      running: this.isRunning,
      maximumSpeed: this.maximumSpeed,
      maxSyncIterations: this.maxSyncIterations,
      asyncDelay: this.asyncDelay,
      eventTime: this.eventTime,
      wallCursor: this.wallNow(),
      lastTickOffset: this.lastTickTime - now,
      tick: this.continuationSuspended ? this.suspendedTick : tick,
      eventWake: this.continuationSuspended ? this.suspendedEventWake : eventWake,
      propagationWaves: this.propagationWaves,
      dirty: [...this.dirtyNodes].map((id) => this.graph.nodeIdText(id)),
      events: this.timedEvents.capture(),
      edges: [...this.graph.edges.values()].map((edge) => this.graph.edgeDto(edge)),
      nodes: [...this.graph.boundaryNodes()].map((node) => ({
        id: node.idText,
        type: node.type,
        runtimeKey: node.runtimeKey,
        state: deepClone(node.state),
        output: node.output,
        elapsedOffset: this.nodeProcessTimes.has(node)
          ? this.nodeProcessTimes.get(node)!.last - now
          : null,
        scheduler:
          node.definition === ClockNode
            ? captureClockScheduler(node.state)
            : node.definition === DelayNode
              ? captureDelayScheduler(node.state)
              : null,
      })),
    };
    return validateSimulationCheckpoint(result, this.graph);
  }

  /** Suspension retains running intent, dirty order and timed work; unlike user Stop. */
  suspendContinuation() {
    const checkpoint = this.captureContinuation();
    this.suspendedProcessNow = this.lastTickTime - checkpoint.lastTickOffset;
    this.suspendedWallCursor = checkpoint.wallCursor;
    this.suspendedTick = checkpoint.tick;
    this.suspendedEventWake = checkpoint.eventWake;
    this.continuationSuspended = true;
    this.cancelPendingTick();
    this.timedEvents.suspend();
    return checkpoint;
  }

  /** Restore without start(), setGraph(), consistency checks or propagation. */
  restoreContinuation(input: unknown, activate = false) {
    this.debugTimers.cancel();
    if (this.isTicking || this.modeBoundaryDepth !== 0)
      throw Error('Cannot restore during processing');
    const c = validateSimulationCheckpoint(input, this.graph);
    this.cancelPendingTick();
    this.timedEvents.suspend();
    for (const node of this.graph.nodes.values()) node.dispose();
    this.timedEvents.clear();
    this.continuationSuspended = true;
    this.maximumSpeedEnabled = c.maximumSpeed;
    this.pendingMaximumSpeed = undefined;
    this.maxSyncIterations = c.maxSyncIterations;
    this.asyncDelay = c.asyncDelay;
    this.eventTime = c.eventTime;
    this.suspendedWallCursor = c.wallCursor;
    this.wallTimeOffset = c.wallCursor - Date.now();
    this.suspendedProcessNow = c.maximumSpeed ? c.eventTime : performance.now();
    this.lastTickTime = this.suspendedProcessNow + c.lastTickOffset;
    this.suspendedTick = c.tick;
    this.suspendedEventWake = c.eventWake;
    this.propagationWaves = c.propagationWaves;
    this.isRunning = c.running;
    this.nodeProcessTimes.clear();
    this.timingOwner = {};
    const nodes = [...this.graph.nodes.values()];
    c.nodes.forEach((saved, index) => {
      const node = nodes[index];
      node.state = deepClone(saved.state);
      node.output = saved.output;
      if (saved.elapsedOffset !== null)
        this.nodeProcessTimes.set(node, {
          last: this.suspendedProcessNow + saved.elapsedOffset,
        });
      // A consumed event may leave an enabled Clock dirty with no issued edge.
      // Restore exactly that private state, rather than discovering work by process().
      if (saved.scheduler || node.definition === DelayNode)
        node.restoreContinuationTrigger(
          () => this.triggerUpdate(node.id),
          this.createTiming(undefined, node, saved.scheduler ?? undefined),
          Boolean(saved.scheduler),
        );
    });
    this.dirtyNodes.clear();
    const byText = new Map(nodes.map((node) => [node.idText, node.id]));
    for (const id of c.dirty) this.dirtyNodes.add(byText.get(id)!);
    this.timedEvents.restoreOrder(c.events);
    if (activate) this.activateContinuation();
  }

  /** Excludes transfer/offline time, then arms captured work without reinitialization. */
  activateContinuation() {
    if (!this.continuationSuspended) return;
    if (!this.maximumSpeed) {
      const shift = performance.now() - this.suspendedProcessNow;
      this.lastTickTime += shift;
      for (const record of this.nodeProcessTimes.values()) record.last += shift;
      this.wallTimeOffset = this.suspendedWallCursor - Date.now();
    }
    this.continuationSuspended = false;
    const armTick = () => {
      if (this.isRunning && this.suspendedTick)
        this.armContinuationTick(this.suspendedTick.remaining, this.suspendedTick);
    };
    // Preserve equal-ready host work order as well as equal-deadline event order.
    if (
      this.suspendedEventWake &&
      this.suspendedTick &&
      this.suspendedEventWake.order < this.suspendedTick.order
    ) {
      this.timedEvents.resume(this.suspendedEventWake);
      armTick();
    } else {
      armTick();
      this.timedEvents.resume(this.suspendedEventWake);
    }
    this.suspendedTick = null;
    this.suspendedEventWake = null;
  }

  get hasSuspendedContinuation() {
    return this.continuationSuspended;
  }

  setAsyncScheduling(value: boolean) {
    // Legacy support or toggle
    if (value) {
      this.maxSyncIterations = 1;
      this.asyncDelay = 0;
    } else {
      this.maxSyncIterations = 5000;
      this.asyncDelay = 0;
    }
  }

  setConfig(config: {
    maxSyncIterations?: number;
    asyncDelay?: number;
    maximumSpeed?: boolean;
  }) {
    if (config.maxSyncIterations !== undefined)
      this.maxSyncIterations = config.maxSyncIterations;
    if (config.asyncDelay !== undefined) this.asyncDelay = config.asyncDelay;
    if (config.maximumSpeed !== undefined) this.setMaximumSpeed(config.maximumSpeed);
  }

  triggerUpdate = (sourceNodeId?: NodeHandle) => {
    const owner = authorities.get(this);
    if (owner) {
      owner.fail(Error('Unrouted worker input/control'));
      throw Error('Use ordered worker input');
    }
    const timing =
      this.isRunning && !this.isTicking
        ? performanceDiagnostics.start('simulation-input')
        : undefined;
    this.modeBoundaryDepth++;
    try {
      if (this.continuationSuspended)
        throw Error('Activate suspended continuation before input');
      if (!this.isRunning) return;

      if (!this.isTicking) {
        const receivedAt = this.wallNow();
        // Finish the earlier input and its overdue transport events before
        // synchronizing this input from tile state. A browser stall must not
        // reorder reset/capture pulses on different control paths.
        if (
          this.timerId !== null &&
          this.asyncDelay === 0 &&
          this.propagationWaves < 10_000
        )
          this.drainUntilStable();
        this.timedEvents.catchUp(receivedAt, () => {
          if (this.propagationWaves >= 10_000 && this.dirtyNodes.size > 0) return false;
          this.drainUntilStable();
          return this.dirtyNodes.size === 0;
        });
        this.eventTime = receivedAt;
      }

      if (sourceNodeId) {
        this.dirtyNodes.add(sourceNodeId);
      } else {
        this.markAllNodesDirty();
      }

      this.scheduleTick();
    } finally {
      this.finishModeBoundary();
      if (timing !== undefined)
        performanceDiagnostics.finish('simulation-input', timing, this.graph.nodes.size);
    }
  };

  private scheduleTick() {
    if (this.timerId !== null) return; // Already scheduled

    if (!this.isTicking) {
      this.tick();
    }
  }

  tick = () => {
    if (this.continuationSuspended) return;
    this.modeBoundaryDepth++;
    try {
      if (!this.isRunning) return;
      this.cancelPendingTick();

      const now = this.now();
      const deltaTime = now - this.lastTickTime;
      this.lastTickTime = now;

      this.isTicking = true;
      try {
        let iterations = 0;
        const limit = Math.max(1, this.maxSyncIterations);

        // Accumulate processed nodes across iterations so onPostUpdate
        // (syncStateToTiles) is called ONCE with the final converged state,
        // not per-iteration with intermediate propagation state.
        const allProcessed = new ProcessedNodes(this.graph);

        do {
          this.processOnce(deltaTime, allProcessed, now);
          iterations++;
        } while (this.dirtyNodes.size > 0 && iterations < limit);

        this.publishProcessed(allProcessed);

        if (this.dirtyNodes.size > 0) {
          this.armContinuationTick(this.asyncDelay);
        }
      } finally {
        this.isTicking = false;
        if (this.dirtyNodes.size === 0) this.propagationWaves = 0;
        this.timedEvents.wake();
      }
    } finally {
      this.finishModeBoundary();
    }
  };

  attachTriggers(retained: ReadonlySet<NodeHandle> = new Set()) {
    const now = this.now();
    for (const node of this.graph.nodes.values()) {
      if (retained.has(node.id) && (!node.definition.setup || node.cleanup)) continue;
      if (!this.nodeProcessTimes.has(node))
        this.nodeProcessTimes.set(node, { last: now });
      node.setTrigger(
        () => this.triggerUpdate(node.id),
        this.createTiming(undefined, node),
      );
    }
  }

  /** Shared active/passive timing domain; passive scheduling never arms a host task. */
  private createTiming(
    schedule: SimulationTiming['schedule'] = this.timedEvents.schedule,
    node?: LogicNodeInstance,
    restoredScheduler?: NodeSchedulerCheckpoint,
  ): SimulationTiming {
    return {
      clockOwner: this.clockOwner,
      resumable: schedule === this.timedEvents.schedule,
      restoredScheduler,
      now: () => this.eventTime,
      wallNow: () => this.wallNow(),
      fromWallTime: (at, wallAt) =>
        this.maximumSpeed
          ? this.eventTime + (at - wallAt)
          : this.wallTimeOffset === 0
            ? at
            : at + this.wallTimeOffset,
      captureTimeMapping: () => {
        const maximum = this.maximumSpeed;
        const logicalAt = this.eventTime;
        const offset = this.wallTimeOffset;
        return (at, wallAt) =>
          maximum ? wallAt + (at - logicalAt) : offset === 0 ? at : at - offset;
      },
      schedule: (at, callback) =>
        schedule === this.timedEvents.schedule
          ? this.timedEvents.schedule(at, callback, node?.idText)
          : schedule(at, callback),
    };
  }

  private elapsedForNode(entry: SimulationNode, node: LogicNodeInstance, now: number) {
    // The map preserves records across rebuilds; stable handles bypass it on hot ticks.
    let timing =
      entry.node === node && entry.timingOwner === this.timingOwner
        ? entry.timing
        : undefined;
    if (!timing) {
      timing = this.nodeProcessTimes.get(node);
      if (!timing) {
        timing = { last: now };
        this.nodeProcessTimes.set(node, timing);
      }
      if (entry.node === node) {
        entry.timingOwner = this.timingOwner;
        entry.timing = timing;
      }
    }
    const elapsed = Math.max(0, now - timing.last);
    timing.last = now;
    return elapsed;
  }

  /**
   * Run one simulation iteration: process dirty nodes and propagate downstream.
   * Adds processed node IDs to `accumulator`.
   */
  private processOnce(
    deltaTime: number,
    accumulator: ProcessedNodes,
    tickTime?: number,
    diagnostics = true,
  ) {
    // A free-running feedback clock never settles. After the same bounded
    // search used by drainUntilStable, let timed events run between its waves
    // so an oscillator cannot starve unrelated delay circuits forever.
    this.propagationWaves = Math.min(10_000, this.propagationWaves + 1);
    this.onPreUpdate?.(this.dirtyNodes);

    const graph = this.graph;
    const revision = graph.topologyRevision;
    const nodesToProcess =
      this.dirtyNodes instanceof SimulationWorkSet
        ? this.dirtyNodes.takeNodes(graph)
        : Array.from(this.dirtyNodes, (id) => graph.getSimulationNode(id));
    if (!(this.dirtyNodes instanceof SimulationWorkSet)) this.dirtyNodes.clear();
    const timing = diagnostics && this.debugTimers.enabled && !this.diagnosticsSuppressed;
    if (timing && nodesToProcess.length) this.debugTimers.beginWave();
    const evaluated = this.evaluateBooleanWave?.(graph, nodesToProcess);
    const inputs: boolean[] = [];
    // Capture every input before processing any node, including feedback loops.
    for (const entry of nodesToProcess) {
      inputs.push(evaluated ? false : graph.getSimulationInput(entry));
    }

    for (let index = 0; index < nodesToProcess.length; index++) {
      const entry = nodesToProcess[index];
      if (!entry) continue;
      const nodeId = entry.id;
      // Extensions can edit a graph during processing. Keep the original
      // snapshot of inputs, but resolve node lifetime and downstream edges fresh.
      const unchanged =
        this.graph === graph &&
        graph.isTopologyTracked &&
        graph.topologyRevision === revision;
      const node = unchanged ? entry.node : this.graph.nodes.get(nodeId);
      if (!node) continue;

      const input = inputs[index];
      const oldOutput = node.output;
      const elapsed =
        tickTime === undefined ? deltaTime : this.elapsedForNode(entry, node, tickTime);
      if (
        timing &&
        (node.type === 'DEBUG_TIMER_START' || node.type === 'DEBUG_TIMER_END')
      )
        this.debugTimers.observe(node, input);
      if (evaluated) node.output = evaluated[index];
      else node.process(input, elapsed);
      this.nodeEvaluationCount++;

      if (node.output !== oldOutput) {
        if (
          this.dirtyNodes instanceof SimulationWorkSet &&
          this.graph === graph &&
          graph.isTopologyTracked &&
          graph.topologyRevision === revision
        ) {
          const targets = graph.getSimulationTargets(entry);
          for (let index = entry.targetStart; index < entry.targetEnd; index++)
            this.dirtyNodes.addNode(targets[index]);
        } else {
          for (const edge of this.graph.getEdgesFrom(nodeId))
            this.dirtyNodes.add(edge.targetNodeId);
        }
      }

      accumulator.addNode(entry, this.graph);
    }
    if (timing) this.debugTimers.finishWave();
  }

  /**
   * Synchronously drain all remaining dirty nodes until stable.
   * Called by rebuildGraph to ensure full convergence before the MobX
   * action boundary — preventing partially-converged state from rendering.
   */
  drainUntilStable(diagnostics = true) {
    const timing = this.dirtyNodes.size
      ? performanceDiagnostics.start('simulation-drain')
      : undefined;
    this.modeBoundaryDepth++;
    try {
      this.cancelPendingTick();
      if (this.dirtyNodes.size === 0) return;

      const hardLimit = 10_000;
      let iterations = 0;
      const allProcessed = new ProcessedNodes(this.graph);

      // Inputs may have queued behind an asynchronous propagation wave. Keep
      // pre-update synchronization active so draining observes every queued edit.
      while (this.dirtyNodes.size > 0 && iterations < hardLimit) {
        this.processOnce(0, allProcessed, undefined, diagnostics);
        iterations++;
      }

      if (this.dirtyNodes.size > 0) {
        console.warn(
          `[LogicSimulation] drainUntilStable hit ${hardLimit} iteration limit with ${this.dirtyNodes.size} nodes still dirty (possible oscillation)`,
        );
      }

      if (allProcessed.size > 0) {
        this.publishProcessed(allProcessed);
      }

      // Feedback clocks cannot converge. The drain canceled their pending tick,
      // so yield the remaining work back to the scheduler instead of freezing it.
      // Do not tick synchronously here: that would extend the bounded drain.
      if (this.isRunning && this.dirtyNodes.size > 0 && this.timerId === null) {
        this.armContinuationTick(this.asyncDelay);
      }
      if (this.dirtyNodes.size === 0) this.propagationWaves = 0;
      this.timedEvents.wake();
    } finally {
      this.finishModeBoundary();
      if (timing !== undefined)
        performanceDiagnostics.finish('simulation-drain', timing, this.graph.nodes.size);
    }
  }

  /**
   * Public update method for manual stepping and external callers.
   * Runs one iteration and immediately syncs tiles.
   */
  update(deltaTime: number) {
    this.modeBoundaryDepth++;
    try {
      const processed = new ProcessedNodes(this.graph);
      this.processOnce(deltaTime, processed);
      this.publishProcessed(processed);

      if (this.dirtyNodes.size > 0) {
        this.scheduleTick();
      }
      this.onFlushVisuals?.();
    } finally {
      this.finishModeBoundary();
    }
  }

  step(deltaTime: number) {
    if (authorities.has(this)) throw Error('Use ordered worker Step');
    if (!this.isRunning) {
      if (this.maximumSpeed || this.wallTimeOffset !== 0) {
        // Keep native Delay in this simulation's logical domain while paused.
        // Clock remains detached and keeps its existing delta-based manual step.
        const passiveTiming = this.createTiming(() => () => {});
        for (const node of this.graph.nodes.values()) {
          if (node.definition === DelayNode) node.setTrigger(() => {}, passiveTiming);
        }
      }
      this.eventTime = this.maximumSpeed
        ? this.eventTime + (Number.isFinite(deltaTime) ? Math.max(0, deltaTime) : 0)
        : this.wallNow();
      this.markAllNodesDirty();
    }
    this.update(deltaTime);
  }
}
